/* ============================================================
   hoh-contract-away.js · R778 · AWAY MODE, THE PURE HALF
   FramedFantasy Hall of Heroes · Sep 2026

   Jan (R778): "an auto-battler should not need you watching." A party with
   Away mode keeps fighting while its player is gone, at the pace a round
   takes to watch at 1x, for up to twelve hours a trip. This file holds the
   parts of that which are arithmetic and nothing else:

     - the pace table and the clock (roundSeconds, roundsDue,
       secondsUntilRound), so "About 12 min left" and "how many rounds are
       due" are one function of the live wave plan;
     - the three orders, normalised once (normalizeOrders, toWire);
     - how long the next segment may be (segmentLength, benchStopLength);
     - the headless Auto-cast (autopilotPlays): the plays a watching phone
       with Auto-cast and Auto-cast items on would have queued, derived from
       the tape instead of from a screen;
     - the E.run options for a segment, built from the state door and the
       prepare reply exactly the way the watched fight builds them
       (simOptsFrom).

   ⛔ NO DOM, NO Date.now, NO Math.random. Every answer is a function of its
   arguments, because the catch-up loop and the harness must both reach the
   same plays for the same segment. The engine arrives as an argument (E),
   so this file never reaches for window.HOHContract itself.
   ⛔ THE ENGINE IS THE AUTHORITY FOR EVERY RULE IT EXPORTS. Item ids come
   from E.unlimitedAutoItems/E.itemIdFor, card ids from the tape itself, the
   play cap from E.UNLIMITED_PLAY_CAP_V65, the clock from E.wavePlan. A
   number typed twice is how R560 happened.
   Loaded by sections/hall-of-heroes.liquid after the engine and content,
   before the data layer and the UI. Node: require() returns the same api.
   ============================================================ */
(function (root) {
  'use strict';

  /* R778 (Jan): "a round takes about as long as watching it at 1x". Seconds
     per round kind, plus the stop's shop beat after a stop round. Versioned by
     name so a later retune is PACE_V2 and a trip already running can keep the
     table it started on. */
  var PACE_V1 = { fight: 85, mini: 110, boss: 140, stop: 20 };
  /* the SQL's TRIP_CAP (43200) and GRACE (2 minutes), the same two numbers */
  var TRIP_CAP_S = 43200;
  var GRACE_S = 120;
  /* the SQL's orders grammar: stop_before 2..9999 or null, bench_left 0..9 */
  var STOP_BEFORE_MAX = 9999;
  var BENCH_LEFT_MAX = 9;
  var ROUND_MAX = 9999999;
  /* R772's Auto-cast items order. Second Wind waits for a third of max
     Health; the other four go at the first free turns of the first fight. */
  var ITEM_ORDER = ['mend', 'bolt', 'ally', 'horn', 'goblin'];
  var AT_ONCE = ['bolt', 'ally', 'horn', 'goblin'];
  /* the R773 wire cap, only when the engine is too old to say it */
  var PLAY_CAP_FALLBACK = 128;
  /* the UI's healthMaxFrom key list for side A, so "a third of max" here
     reads the same max the orb's ring reads on a watched fight */
  var MAX_KEYS_A = ['max_a', 'maxA', 'max_ha', 'ha_max', 'ta_max'];

  function has(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }
  function finite(v) { return typeof v === 'number' && isFinite(v); }
  function intOrNull(v) {
    if (v == null || v === '' || typeof v === 'boolean') return null;
    var n = Number(v);
    return isFinite(n) && Math.floor(n) === n ? n : null;
  }

  /* ---------------- the clock ---------------- */
  function roundSeconds(E, round, engineVer) {
    var plan = null;
    try { plan = E.wavePlan(Math.max(1, round | 0), engineVer); } catch (_) { plan = null; }
    var kind = plan && plan.kind;
    var s = kind === 'boss' ? PACE_V1.boss : kind === 'mini' ? PACE_V1.mini : PACE_V1.fight;
    if (plan && plan.stopAfter) s += PACE_V1.stop;
    return s;
  }
  /* whole rounds from fromRound whose cumulative pace fits the time away,
     never more than twelve hours of them */
  function roundsDue(E, fromRound, seconds, engineVer) {
    var budget = finite(seconds) ? Math.min(Math.max(0, seconds), TRIP_CAP_S) : 0;
    var start = Math.max(1, fromRound | 0), n = 0, total = 0;
    while (start + n <= ROUND_MAX) {
      var s = roundSeconds(E, start + n, engineVer);
      if (total + s > budget) break;
      total += s; n++;
    }
    return n;
  }
  function secondsUntilRound(E, fromRound, n, engineVer) {
    var start = Math.max(1, fromRound | 0);
    var count = Math.max(0, Math.min(n | 0, ROUND_MAX - start + 1, 100000));
    var total = 0;
    for (var i = 0; i < count; i++) total += roundSeconds(E, start + i, engineVer);
    return total;
  }

  /* ---------------- the orders ---------------- */
  /* One reader for both spellings (the Round menu's camelCase and the door's
     snake_case). Defaults are Jan's: Never, stop when the bench is empty,
     Always Second Wind. An explicit null bench order is the unticked box;
     a missing one is the default. */
  function pick(o, a, b) {
    if (has(o, a)) return o[a];
    if (has(o, b)) return o[b];
    return undefined;
  }
  function normalizeOrders(o) {
    o = (o && typeof o === 'object') ? o : {};
    var sb = intOrNull(pick(o, 'stopBefore', 'stop_before'));
    var bl = pick(o, 'benchLeft', 'bench_left'), bench = 0;
    if (bl === null) bench = null;
    else if (bl !== undefined) {
      var b = Number(bl);
      bench = isFinite(b) ? Math.max(0, Math.min(BENCH_LEFT_MAX, Math.floor(b))) : 0;
    }
    return {
      stopBefore: sb != null && sb >= 2 && sb <= STOP_BEFORE_MAX ? sb : null,
      benchLeft: bench,
      shop: pick(o, 'shop', 'shop') === 'ally' ? 'ally' : 'mend'
    };
  }
  function toWire(orders) {
    var n = normalizeOrders(orders);
    return { stop_before: n.stopBefore, bench_left: n.benchLeft, shop: n.shop };
  }

  /* ---------------- segment length ---------------- */
  /* how many rounds the next child may hold. 0 means "not yet": nothing is
     due, or the next round is the one the player said to stop before. */
  function segmentLength(o) {
    o = o || {};
    var def = Math.max(0, o.defaultCount | 0);
    var due = o.dueRounds === Infinity ? def : (finite(o.dueRounds) ? Math.max(0, Math.floor(o.dueRounds)) : 0);
    var n = Math.min(def, due);
    var sb = intOrNull(o.stopBefore);
    if (sb != null && sb >= 2) n = Math.min(n, Math.max(0, sb - Math.max(1, o.ws | 0)));
    return Math.max(0, n);
  }
  /* every stack copy is its own body (a Goblin Stack of 5 is five entries),
     and this is the same list the server writes and the state door returns */
  function benchLeft(sim) {
    return sim && Array.isArray(sim.recruits) ? sim.recruits.length : 0;
  }
  function lastFightEnd(sim) {
    var ev = (sim && sim.events) || [];
    for (var i = ev.length - 1; i >= 0; i--) if (ev[i] && ev[i].e === 'fight_end') return ev[i];
    return null;
  }
  /* the engine breaks after the first uncleared fight, so the last fight_end
     is the verdict: a loss ends the run whatever the orders say */
  function segmentLost(sim) {
    var end = lastFightEnd(sim);
    return !!end && !end.cleared;
  }

  /* ---------------- the headless Auto-cast ---------------- */
  function engineVerOf(E, opts) {
    return opts && opts.engineVer != null ? (opts.engineVer | 0) : ((E && E.ENGINE_VERSION) | 0);
  }
  function playCapOf(E, ctx) {
    var cap = (E && (E.UNLIMITED_PLAY_CAP_V65 | 0)) || PLAY_CAP_FALLBACK;
    if (ctx && (ctx.cap | 0) > 0) cap = Math.min(cap, ctx.cap | 0);
    return cap;
  }
  function isBlessing(id) { return /^mb[0-9]/.test(String(id || '')); }
  function itemKindOf(E, id) {
    try { return (E && typeof E.itemKind === 'function') ? (E.itemKind(id) || '') : ''; } catch (_) { return ''; }
  }
  /* the copies the saved bag really holds, in the engine's own id order
     (mend, mend2, mend3...). unlimitedAutoItems is the judge's id set for
     automatic casts, so an id outside it would be skipped without a trace. */
  function itemStock(E, items, ver) {
    var out = {}, allowed = null;
    ITEM_ORDER.forEach(function (k) { out[k] = []; });
    try { allowed = E.unlimitedAutoItems(items || {}, ver); } catch (_) { allowed = null; }
    if (!allowed || typeof E.itemIdFor !== 'function') return out;
    ITEM_ORDER.forEach(function (kind) {
      var n = Math.max(0, Math.min(32, (items && items[kind]) | 0));
      for (var i = 0; i < n; i++) {
        var id = E.itemIdFor(kind, i);
        if (has(allowed, id)) out[kind].push(id);
      }
    });
    return out;
  }
  /* the tape already writes fight-qualified ids (f12.c0, f12.d3). A bare
     local id is qualified with the fight it arrived in; anything else is
     not a card. */
  function cardIdFrom(E, round, id) {
    var s = String(id || '');
    if (!s) return '';
    if (E.fightCardPlayParse(s)) return s;
    if (E.cardPlayParse(s) && round > 0) return E.fightQualifiedCardId(round, s);
    return '';
  }
  function healthMaxA(e) {
    for (var i = 0; i < MAX_KEYS_A.length; i++) {
      var v = e[MAX_KEYS_A[i]];
      if (v != null && isFinite(+v)) return Math.max(0, v | 0);
    }
    if (e.max_hp != null && (e.s != null ? e.s === 'A' : true)) return Math.max(0, e.max_hp | 0);
    var side = e.e === 'bench_chain_land' ? e.target : e.s;
    if (e.max_health != null && side === 'A') return Math.max(0, e.max_health | 0);
    return 0;
  }
  function firstFree(from, taken) {
    var at = Math.max(1, from | 0);
    while (taken[at]) at++;
    return at;
  }
  function copyPlay(p) {
    var out = { id: String(p.id), atA: p.atA | 0, auto: p.auto || '', name: String(p.name || ''), cls: String(p.cls || '') };
    if (Array.isArray(p.names)) out.names = p.names.slice(0, 3);
    if ((p.w | 0) > 0) { out.w=p.w | 0; out.fight=p.fight | 0; }
    if (/^bolt(?:[2-9]|10)?$/.test(out.id) && typeof p.ownerId==='string')out.ownerId=p.ownerId;
    return out;
  }
  function simCopy(list) {
    var out = [];
    if (!Array.isArray(list)) return out;
    for (var i = 0; i < list.length; i++) {
      var src = list[i];
      if (!src || typeof src !== 'object') { out.push(src); continue; }
      var o = {};
      for (var k in src) if (has(src, k)) o[k] = src[k];
      out.push(o);
    }
    return out;
  }
  /* The ally and the horn show a Hall face, and only the page knows the Hall.
     ctx.allyCls(kind, id) answers a class string or {cls, name, names}; no
     answer means that copy stays in the bag, the same as a watched fight
     with the Hall still loading. Called once per queued copy, in queue
     order, so it must be deterministic too. */
  function faceFor(ctx, kind, id) {
    if (!ctx || typeof ctx.allyCls !== 'function') return null;
    var r = null;
    try { r = ctx.allyCls(kind, id); } catch (_) { r = null; }
    if (typeof r === 'string') r = { cls: r };
    if (!r || typeof r !== 'object') return null;
    var cls = String(r.cls || (kind === 'horn' ? 'Paladin' : '')).slice(0, 40);
    if (!cls) return null;
    return {
      cls: kind === 'horn' ? 'Paladin' : cls,
      name: String(r.name || '').slice(0, 40),
      names: Array.isArray(r.names) ? r.names.slice(0, 3).map(function (n) { return String(n || '').slice(0, 40); }) : null
    };
  }
  /* ---- the rule, one tape event at a time ----
     A card: a `cardgen` (s:'A') or a `fell` that left a copy, cast two turns
     after the turn it arrived on (the phone's autoCardReady waits one whole
     turn, then takes the next free one). Second Wind: the first moment the
     tape's health is at or under a third of max with no Second Wind already
     on its way, cast on the next turn (R772's switch reads the orb the same
     way). Every play takes a turn nothing else holds: the reader drops an
     item that shares a turn, and the phone never stacks two either. */
  function newWalker(E, plays, st) {
    var w = { E: E, st: st, queued: {}, taken: {}, mends: [], spent: {},
              round: 0, lastTurn: 0, ha: null, max: 0 };
    for (var i = 0; i < plays.length; i++) commitPlay(w, plays[i]);
    return w;
  }
  function commitPlay(w, p) {
    w.queued[p.id] = p;
    if (!isBlessing(p.id)) w.taken[p.atA | 0] = 1;
    if (itemKindOf(w.E, p.id) === 'mend') w.mends.push(p.id);
  }
  function observe(w, e) {
    if (e.e === 'fight_start') w.round = e.n | 0;
    else if (e.e === 'aturn') w.lastTurn = e.n | 0;
    var m = healthMaxA(e);
    if (m > 0) w.max = m;
    if (finite(e.ha)) w.ha = e.ha;
    else if (e.e === 'lock' && finite(e.ta)) w.ha = e.ta;
    if (e.e === 'play' && !e.blocked && itemKindOf(w.E, e.id)) w.spent[e.id] = 1;
  }
  function cardWanted(w, e) {
    if (e.s !== 'A' || !((e.e === 'cardgen' && e.id) || (e.e === 'fell' && e.card))) return null;
    var cid = cardIdFrom(w.E, w.round, e.e === 'cardgen' ? e.id : e.card);
    if (!cid || w.queued[cid]) return null;
    if(w.st.horizon && w.round<=w.st.horizon.afterRound)return null;
    return { id: cid, atA: firstFree(Math.max(w.lastTurn + 2,w.st.firstAuto), w.taken), auto: 'mobile', name: '', cls: '' };
  }
  function mendWanted(w) {
    var stock = w.st.stock.mend;
    if (!stock.length || !(w.max > 0) || w.ha == null || w.ha * 3 > w.max) return null;
    if(w.st.horizon && (w.round<=w.st.horizon.afterRound || w.lastTurn<w.st.horizon.atA))return null;
    for (var q = 0; q < w.mends.length; q++) if (!w.spent[w.mends[q]]) return null;
    for (var s = 0; s < stock.length; s++) {
      if (!w.queued[stock[s]] && !w.spent[stock[s]]) {
        return { id: stock[s], atA: firstFree(Math.max(w.lastTurn + 1,w.st.firstAuto), w.taken), auto: '', name: '', cls: '' };
      }
    }
    return null;
  }
  function tapeTurns(events) {
    var at = {}, last = 0;
    for (var i = 0; i < events.length; i++) {
      var t = events[i];
      if (t && t.e === 'aturn') { at[t.n | 0] = i; if ((t.n | 0) > last) last = t.n | 0; }
    }
    return { at: at, last: last };
  }
  /* A play with atA t fires at the top of the first beat after the tape's
     aturn t-1, and playSeed keeps its dice off the shared stream, so every
     event up to that aturn is byte identical with or without it. This is
     that aturn's index: the last event a tape holding (or missing) the play
     can vouch for. -1 when it fires on the first beat, Infinity when this
     tape never reaches it. */
  function horizonOf(play, turns) {
    var before = (play.atA | 0) - 1;
    if (before < 1) return -1;
    if (before > turns.last || turns.at[before] == null) return Infinity;
    return turns.at[before];
  }
  function samePlay(a, b) {
    return a.id === b.id && (a.atA | 0) === (b.atA | 0) && (a.auto || '') === (b.auto || '');
  }
  /* THE RULE IS SEQUENTIAL: simulate, answer the first unanswered trigger,
     simulate again (the spec's definition, and the `sequential` mode below).
     Run literally that is one simulation per card, which measured 4 to 10
     seconds a segment past Round 20. So a tape is read as far as it can be
     trusted instead:
       - `confirm`: the pass before guessed the next cards from its own tape
         (Q). This tape was simulated WITH them. Walking it from the start,
         a trigger that asks for exactly the next guess, at an index no
         unconfirmed guess could have touched yet, is the sequential answer.
       - `decide`: a trigger nobody guessed (a Second Wind, or a card the
         guess got wrong) is answered as the sequential rule would, and the
         guesses still unconfirmed are dropped.
     Either way the walk stops at the first index an unconfirmed or new play
     could have changed. What it kept is exactly what one-play-per-sim keeps;
     the harness runs all three modes and compares them. */
  function walkTape(E, events, C, Q, st, maxAdds) {
    var w = newWalker(E, C, st), turns = tapeTurns(events);
    var unconf = Q.slice(), dropped = Infinity, fresh = Infinity, added = [], decided = 0;
    function horizon() {
      var h = Math.min(dropped, fresh);
      for (var j = 0; j < unconf.length; j++) h = Math.min(h, horizonOf(unconf[j], turns));
      return h;
    }
    var h = horizon(), reachedEnd = true;
    for (var i = 0; i < events.length; i++) {
      if (i > h || C.length + added.length >= st.cap || added.length >= maxAdds) { reachedEnd = false; break; }
      var e = events[i];
      if (!e || typeof e !== 'object') continue;
      observe(w, e);
      for (var pass = 0; pass < 2; pass++) {
        if (C.length + added.length >= st.cap || added.length >= maxAdds) break;
        var want = pass === 0 ? cardWanted(w, e) : mendWanted(w);
        if (!want) continue;
        if (unconf.length && samePlay(unconf[0], want)) unconf.shift();
        else {
          for (var j = 0; j < unconf.length; j++) dropped = Math.min(dropped, horizonOf(unconf[j], turns));
          unconf = [];
          fresh = Math.min(fresh, horizonOf(want, turns));
          decided++;
        }
        commitPlay(w, want);
        added.push(want);
        h = horizon();
      }
    }
    return { added: added, clean: reachedEnd && !unconf.length && !decided };
  }
  /* the next guesses: every card this tape still asks for, in order, as if
     nothing before it will move. The guess stops at the first Second Wind,
     because a heal moves everything after it and is never guessed. */
  function guessCards(E, events, C, st) {
    var w = newWalker(E, C, st), out = [];
    for (var i = 0; i < events.length && C.length + out.length < st.cap; i++) {
      var e = events[i];
      if (!e || typeof e !== 'object') continue;
      observe(w, e);
      var card = cardWanted(w, e);
      if (card) { commitPlay(w, card); out.push(card); }
      if (mendWanted(w)) break;
    }
    return out;
  }
  function sortPlays(list) {
    return list.slice().sort(function (a, b) { return (a.atA - b.atA) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0); });
  }
  /* autopilotPlays(E, opts, ctx) -> { plays, sim, sims }
     opts: the E.run options of one Unlimited segment (simOptsFrom). Plays
     already in opts.plays are kept and count as queued. ctx: { allyCls,
     cap, mode } where mode is 'guess' (default), 'batch' (no guesses) or
     'sequential' (one play per simulation, the definition). The returned
     sim is E.run(opts with exactly these plays). */
  function autopilotPlays(E, opts, ctx) {
    ctx = ctx || {};
    opts = opts || {};
    var ver = engineVerOf(E, opts), cap = playCapOf(E, ctx);
    var mode = ctx.mode === 'sequential' || ctx.mode === 'batch' ? ctx.mode : 'guess';
    /* PLAYTESTER 2026-10-04: every engine with the upgrade ledger (114 and later; Endless plays 122 and 123 since
       R815/R816) keeps a committed prefix; at 114 only, a pick the player made before leaving was not held at 123 */
    var horizon=ver>=114 && opts._upgradePrefixHorizon114;
    var committed=horizon && (horizon.afterRound>0 || horizon.fightRound>0);
    var st = { stock: itemStock(E, opts.items, ver), cap: cap, horizon:committed?horizon:null, firstAuto:committed?horizon.atA+2:1 };
    var C = [], queued = {}, taken = {};
    (Array.isArray(opts.plays) ? opts.plays : []).forEach(function (p) {
      if (!p || typeof p !== 'object' || !p.id || queued[p.id] || C.length >= cap) return;
      var c = copyPlay(p);
      C.push(c); queued[c.id] = c;
      if (!isBlessing(c.id)) taken[c.atA | 0] = 1;
    });
    /* Volley, Call for Aid, the Horn and the goblins go at once, one per
       turn, in the R772 switch's order (R657: every copy its own beat). */
    AT_ONCE.forEach(function (kind) {
      st.stock[kind].forEach(function (id) {
        if (C.length >= cap || queued[id]) return;
        var play = { id: id, atA: 0, auto: '', name: '', cls: '' };
        if (kind === 'ally' || kind === 'horn') {
          var face = faceFor(ctx, kind, id);
          if (!face) return;
          play.name = face.name; play.cls = face.cls;
          if (face.names) play.names = face.names;
        }
        play.atA = firstFree(st.firstAuto, taken);
        taken[play.atA] = 1; queued[id] = play; C.push(play);
      });
    });
    var base = {}, k;
    for (k in opts) if (has(opts, k)) base[k] = opts[k];
    function simulate(list) {
      base.plays = list.map(copyPlay);
      base.roster = simCopy(opts.roster);
      base.recruits = simCopy(opts.recruits);
      sims++;
      /* R791: a throw from the engine refuses this segment on every replay.
         Tag it at the source so the catch-up stops instead of retrying. */
      try { return E.run(base); }
      catch (e) {
        var err = (e && typeof e === 'object') ? e : new Error(String(e || 'engine_error'));
        try { err.__hohEngine = true; } catch (_) {}
        throw err;
      }
    }
    var sims = 0, sim = null, Q = [], limit = 2 * cap + 8;
    for (;;) {
      sim = simulate(C.concat(Q));
      if (C.length >= cap) break;
      if (sims >= limit) { if (Q.length) sim = simulate(C); break; }
      var events = (sim && sim.events) || [];
      var walked = walkTape(E, events, C, Q, st, mode === 'sequential' ? 1 : Infinity);
      C = C.concat(walked.added);
      if (walked.clean) break;
      if (C.length >= cap) { sim = simulate(C); break; }
      /* a pass that kept nothing tries again with no guesses, so every
         second simulation at worst moves the rule forward */
      Q = mode === 'guess' && walked.added.length ? guessCards(E, events, C, st) : [];
    }
    return { plays: sortPlays(C), sim: sim, sims: sims };
  }
  /* The smallest segment whose last round leaves the bench at or under the
     order (or loses), else maxCount. Each prefix is simulated with its own
     autopilot, seeded with the previous prefix's plays: the tape of rounds
     1..k is the same whatever the segment's length (the harness measures
     this on real segments), so the seed is what a fresh search would find. */
  function benchStopLength(E, opts, left, maxCount, ctx) {
    var max = Math.max(1, maxCount | 0), want = Math.max(0, left | 0);
    var seed = (opts && Array.isArray(opts.plays)) ? opts.plays : [];
    for (var n = 1; n < max; n++) {
      var o = {}, key;
      for (key in opts) if (has(opts, key)) o[key] = opts[key];
      o.roundCount = n;
      o.plays = seed;
      var r = autopilotPlays(E, o, ctx);
      if (segmentLost(r.sim) || benchLeft(r.sim) <= want) return n;
      seed = r.plays;
    }
    return max;
  }

  /* ---------------- E.run options from the doors ---------------- */
  /* simOptsFrom(state, prep): the watched fight builds its options in three
     steps, and this is those steps folded into one with nothing on S:
       unlimAdoptState(state)   S.run from the state door, carry in S.run._unlim
       _adoptPrepared(prep)     every key of prep.run merged over S.run
       _simOptsUnlimited()      the mapping below, verbatim
     So roster, recruits, seed, window and foe (the frozen best round, the
     Round 20 flag and a solo trial) come from prep.run when it carries them,
     and the carry (health, max, armor, ward, lock, fallen, items) always
     comes from the state door. plays starts empty: autopilotPlays fills it.
     E is optional and only stands in for runEngineVersion()'s last fallback
     when neither row names an engine (a live row always does). */
  function frozenBest(row) {
    var v = row && row.best_round_at_start;
    return (typeof v === 'number' && Math.floor(v) === v && v >= 0 && v <= 2147483647) ? v : null;
  }
  function frozenClear20(row) {
    var v = row && (row.cleared20_at_start != null ? row.cleared20_at_start : row.cleared20AtStart);
    return typeof v === 'boolean' ? v : undefined;
  }
  /* unlimItemsShape, the UI's own copy of the bag shape */
  function itemsShape(items, ver) {
    var it = items || {};
    var out = { mend: it.mend | 0, bolt: it.bolt | 0, ally: it.ally | 0 };
    if ((ver | 0) >= 72) out.horn = Math.max(0, Math.min(3, it.horn | 0));
    if ((ver | 0) >= 101) out.goblin = Math.max(0, Math.min(1, it.goblin | 0));
    return out;
  }
  function simOptsFrom(state, prep, E) {
    state = state || {};
    var run = {
      id: state.run_id, seed: state.seed, engine_ver: state.engine_ver | 0,
      wave_start: (state.wave_start | 0) || (state.round | 0) || 1,
      wave_count: (state.wave_count | 0) || 1,
      roster: Array.isArray(state.party) ? state.party.slice() : [],
      recruits: Array.isArray(state.recruits) ? state.recruits.slice() : [],
      foe: { mode: 'unlimited', best_round_at_start: frozenBest(state), cleared20_at_start: frozenClear20(state),
             solo_ultimate: state.solo_ultimate || (state.foe && state.foe.solo_ultimate) || undefined }
    };
    var pr = prep && prep.run, k;
    if (pr && typeof pr === 'object') for (k in pr) if (has(pr, k)) run[k] = pr[k];
    var ver = (run.engine_ver | 0) || ((E && E.ENGINE_VERSION) | 0), foe = run.foe || {}, bestAtStart = frozenBest(foe);
    return {
      mode: 'unlimited',
      engineVer: ver,
      seed: run.seed,
      aelricRevisit: ver >= 80 ? bestAtStart >= 21 : undefined,
      bestRoundAtStart: ver >= 88 ? bestAtStart : undefined,
      cleared20AtStart: ver >= 92 ? foe.cleared20_at_start : undefined,
      soloUltimate: ver >= 98 && foe && foe.solo_ultimate ? Object.assign({}, foe.solo_ultimate) : undefined,
      roster: simCopy(run.roster || []),
      recruits: simCopy(run.recruits || []),
      roundStart: (run.wave_start | 0) || 1,
      roundCount: (run.wave_count | 0) || 1,
      plays: [],
      items: itemsShape(state.items, state.engine_ver | 0),
      carryA: state.hp | 0,
      maxA: state.max_hp | 0,
      armorA: state.armor | 0,
      wardA: state.ward | 0,
      lockA: state.lock_a || null,
      fallenHero: Array.isArray(state.fallen_hero) ? state.fallen_hero.slice() : []
    };
  }

  var api = {
    VERSION: 1,
    PACE_V1: PACE_V1, TRIP_CAP_S: TRIP_CAP_S, GRACE_S: GRACE_S,
    STOP_BEFORE_MAX: STOP_BEFORE_MAX, BENCH_LEFT_MAX: BENCH_LEFT_MAX,
    roundSeconds: roundSeconds, roundsDue: roundsDue, secondsUntilRound: secondsUntilRound,
    normalizeOrders: normalizeOrders, toWire: toWire,
    segmentLength: segmentLength, benchLeft: benchLeft, benchStopLength: benchStopLength,
    lastFightEnd: lastFightEnd, segmentLost: segmentLost,
    autopilotPlays: autopilotPlays, simOptsFrom: simOptsFrom
  };
  root.HOHAway = api;
  if (typeof module === 'object' && module && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
